import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useActivity } from '@/hooks/useActivity'
import { ActivityFilters } from '@/components/activity/ActivityFilters'
import { timeAgo } from '@/lib/utils'
import {
  distinctAgents,
  filterActivity,
  outcomeOf,
  triggerTypeOf,
  type ActivityFilterState,
  type SeverityFilter,
} from '@/lib/activity-utils'
import type { ActivityEntry } from '@/lib/api'

/** Coerce a ?severity= query value to a valid SeverityFilter, defaulting to ALL. */
function normaliseSeverity(value: string | null): SeverityFilter {
  const v = (value ?? '').toUpperCase()
  return v === 'SUCCESS' || v === 'FAILED' ? v : 'ALL'
}

function outcomeVariant(outcome: string) {
  switch (outcome) {
    case 'success': return 'success' as const
    case 'failed':  return 'destructive' as const
    default:        return 'secondary' as const
  }
}

export function ActivityLog() {
  // Pull a wider window so the filters/highlights can work client-side without
  // a new endpoint (the activity API only supports limit + cursor).
  const { data, isLoading, error } = useActivity(200)
  const entries = data?.data ?? []

  // Deep-link support: the dashboard's "last run failed" cards link here with
  // ?agent=<skillName>&severity=FAILED. Seed the filters from the URL once.
  const [searchParams] = useSearchParams()
  const [agent, setAgent] = useState(() => searchParams.get('agent') ?? 'ALL')
  const [severity, setSeverity] = useState<SeverityFilter>(() => normaliseSeverity(searchParams.get('severity')))
  const [rangeKey, setRangeKey] = useState('ALL')
  const [highlightsOnly, setHighlightsOnly] = useState(false)

  const filterState: ActivityFilterState = { agent, severity, rangeKey, highlightsOnly }
  const filtered = filterActivity(entries, filterState)

  // Ensure a deep-linked agent appears in the dropdown even if it has no rows
  // in the current window, so the active filter is visible rather than blank.
  const agents = distinctAgents(entries)
  const agentOptions = agent !== 'ALL' && !agents.includes(agent) ? [agent, ...agents] : agents

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-cmd-error-container p-4">
          <p className="text-sm text-cmd-on-error font-label">FAULT DETECTED: {error.message}</p>
        </div>
      )}

      <ActivityFilters
        agents={agentOptions}
        agent={agent}
        onAgent={setAgent}
        severity={severity}
        onSeverity={setSeverity}
        rangeKey={rangeKey}
        onRange={setRangeKey}
        highlightsOnly={highlightsOnly}
        onToggleHighlights={() => setHighlightsOnly((v) => !v)}
        shown={filtered.length}
        total={entries.length}
      />

      <Card>
        <CardHeader>
          <CardTitle>EVENT LOG</CardTitle>
        </CardHeader>
        <CardContent className="space-y-0">
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-start gap-3 p-3">
                <Skeleton className="mt-0.5 h-5 w-5 shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-2/3" />
                  <Skeleton className="h-2.5 w-1/3" />
                </div>
                <Skeleton className="h-3 w-20 shrink-0" />
              </div>
            ))
          ) : filtered.length === 0 ? (
            <p className="font-label text-sm text-cmd-outline text-center p-6">
              {entries.length === 0 ? 'NO SIGNAL' : 'NO EVENTS MATCH THESE FILTERS'}
            </p>
          ) : (
            filtered.map((entry, i) => <ActivityRow key={entry.id} entry={entry} index={i} />)
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function ActivityRow({ entry, index }: { entry: ActivityEntry; index: number }) {
  const outcome = outcomeOf(entry)
  const durationMs = entry.metadata?.durationMs as number | undefined
  const triggerType = triggerTypeOf(entry)

  return (
    <div className={`relative flex items-start gap-3 p-3 ${index % 2 === 0 ? 'bg-cmd-surface' : 'bg-cmd-low'}`}>
      {/* Status bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${outcome === 'failed' ? 'bg-cmd-error' : 'bg-transparent'}`} />

      <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center font-mono text-[10px] font-bold ${outcome === 'failed' ? 'text-cmd-error' : 'text-cmd-outline'}`}>
        {outcome === 'failed' ? '!' : '~'}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white/90">{entry.summary}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="font-label text-[0.65rem] text-primary">{entry.agentName}</span>
          {triggerType && (
            <Badge variant="outline" className="text-[10px]">{triggerType.toUpperCase()}</Badge>
          )}
          <Badge variant={outcomeVariant(outcome)} className="text-[10px]">
            {outcome.toUpperCase()}
          </Badge>
          {durationMs !== undefined && (
            <span className="font-mono text-[10px] text-cmd-outline-variant">{durationMs}ms</span>
          )}
        </div>
      </div>
      <span className="font-mono text-[0.65rem] text-cmd-outline shrink-0 whitespace-nowrap">
        {timeAgo(entry.createdAt)}
      </span>
    </div>
  )
}
